/** Identificadores estáveis para linhas de investimento na proposta de adequações/reformas. */

export type AtividadePropostaAdequacoesId =
  | (typeof ATIVIDADES_PROPOSTA_ELETRICAS)[number]["id"]
  | (typeof ATIVIDADES_PROPOSTA_FV)[number]["id"]
  | (typeof ATIVIDADES_PROPOSTA_INCENDIO_ALARME)[number]["id"]
  | (typeof ATIVIDADES_PROPOSTA_INCENDIO_SDAI)[number]["id"];

export type AtividadePropostaAdequacoesDef = {
  id: AtividadePropostaAdequacoesId;
  label: string;
  /** Linha que reduz o investimento total (valor informado como positivo). */
  isDesconto?: boolean;
  /** Texto auxiliar exibido no formulário da proposta. */
  hint?: string;
};

/** Produto: Reformas e adequações elétricas. */
export const ATIVIDADES_PROPOSTA_ELETRICAS = [
  { id: "ele_projeto_aprovacao", label: "Projeto e Aprovação" },
  { id: "ele_troca_quadro_medicao", label: "Troca do quadro de medição" },
  { id: "ele_adequacao_quadro_medicao", label: "Adequação do quadro de medição" },
  { id: "ele_troca_fiacao", label: "Troca de fiação" },
  { id: "ele_troca_fiacao_entrada", label: "Troca de fiação da entrada" },
  { id: "ele_troca_quadro_distribuicao", label: "Troca de quadro de distribuição" },
  { id: "ele_adequacao_quadro_distribuicao", label: "Adequação de quadro de distribuição" },
  { id: "ele_adequacao_quadro_geral", label: "Adequação de quadro geral" },
  { id: "ele_adequacao_circuitos_iluminacao", label: "Adequações em circuitos de iluminação" },
  { id: "ele_adequacao_circuitos_tomadas", label: "Adequações em circuitos de tomadas" },
  { id: "ele_adequacao_infra_estrutura", label: "Adequações em infra-estrutura elétrica" },
  { id: "ele_quadro_transferencia", label: "Quadro de transferência" },
  { id: "ele_fornecimento_quadro_comando", label: "Fornecimento de Quadro de Comando" },
  { id: "ele_adequacao_quadro_comando", label: "Adequação de quadro de comando" },
  { id: "ele_substituicao_contatora_quadro_comando", label: "Substituição de contatora em quadro de comando" },
  { id: "ele_gerenciador_carga_inteligente", label: "Gerenciador de carga Inteligente" },
  { id: "ele_mobi_box_smart_ev", label: "Mobi box smart - EV" },
  { id: "ele_caixa_bep", label: "Caixa BEP" },
  { id: "ele_projeto_aprovacao_subestacao", label: "Projeto e Aprovação de Subestação" },
  { id: "ele_adequacao_subestacao", label: "Adequação de subestação" },
  { id: "ele_troca_transformador", label: "Troca de transformador" },
  { id: "ele_inspecao_eletrica", label: "Inspeção elétrica" },
  { id: "ele_laudo_spda", label: "Laudo de SPDA e Aterramento" },
  { id: "ele_adequacao_spda", label: "Adequação de SPDA" },
  { id: "ele_mao_obra", label: "Mão de obra" },
  { id: "ele_desconto", label: "Desconto", isDesconto: true },
] as const satisfies readonly AtividadePropostaAdequacoesDef[];

/** Linha de investimento com composição de módulos, inversor(es) e cabos CC. */
export const ID_ATIVIDADE_KIT_FOTOVOLTAICO = "fv_kit_fotovoltaico" as const;

export type ComposicaoKitFotovoltaicoForm = {
  marcaModulo: string;
  moduloId: string;
  quantidadeModulos: string;
  marcaInversor: string;
  inversorId: string;
  quantidadeInversores: string;
};

export const COMPOSICAO_KIT_FOTOVOLTAICO_INICIAL: ComposicaoKitFotovoltaicoForm = {
  marcaModulo: "",
  moduloId: "",
  quantidadeModulos: "1",
  marcaInversor: "",
  inversorId: "",
  quantidadeInversores: "1",
};

export type EquipamentoKitFotovoltaicoRef = {
  id: string;
  marca: string;
  modelo: string;
  potenciaW: number;
};

export type KitFotovoltaicoPdf = {
  modulo_id: string;
  modulo_marca: string;
  modulo_modelo: string;
  modulo_potencia_w: number;
  quantidade_modulos: number;
  inversor_id: string;
  inversor_marca: string;
  inversor_modelo: string;
  inversor_potencia_w: number;
  quantidade_inversores: number;
};

/** Quantidade inteira ≥ 1; vazio ou inválido retorna null. */
export function parseQuantidadeKitFotovoltaico(s: string): number | null {
  const t = String(s ?? "").trim();
  if (!/^\d+$/.test(t)) return null;
  const n = parseInt(t, 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

export function formatPotenciaEquipamentoKit(watts: number): string {
  if (!Number.isFinite(watts) || watts <= 0) return "";
  if (watts >= 1000) {
    const kw = watts / 1000;
    const txt = Number.isInteger(kw)
      ? String(kw)
      : kw.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
    return `${txt} kW`;
  }
  return `${watts} W`;
}

export function textoComposicaoKitFotovoltaico(opts: {
  quantidadeModulos: number;
  modulo: EquipamentoKitFotovoltaicoRef;
  quantidadeInversores: number;
  inversor: EquipamentoKitFotovoltaicoRef;
}): string {
  const potMod = opts.modulo.potenciaW > 0 ? ` (${opts.modulo.potenciaW} W)` : "";
  const potInv = formatPotenciaEquipamentoKit(opts.inversor.potenciaW);
  const potInvTxt = potInv ? ` (${potInv})` : "";
  const modQ =
    opts.quantidadeModulos === 1 ? "1 módulo" : `${opts.quantidadeModulos} módulos`;
  const invQ =
    opts.quantidadeInversores === 1 ? "1 inversor" : `${opts.quantidadeInversores} inversores`;
  return `${modQ} ${opts.modulo.marca} ${opts.modulo.modelo}${potMod}; ${invQ} ${opts.inversor.marca} ${opts.inversor.modelo}${potInvTxt}; cabos CC inclusos (por kit)`;
}

export function resolverComposicaoKitFotovoltaico(
  form: ComposicaoKitFotovoltaicoForm,
  modulos: readonly EquipamentoKitFotovoltaicoRef[],
  inversores: readonly EquipamentoKitFotovoltaicoRef[]
):
  | { ok: true; payload: KitFotovoltaicoPdf; resumo: string }
  | { ok: false; erro: string } {
  const modulo = modulos.find((m) => m.id === form.moduloId);
  const inversor = inversores.find((i) => i.id === form.inversorId);
  const qtdMod = parseQuantidadeKitFotovoltaico(form.quantidadeModulos);
  const qtdInv = parseQuantidadeKitFotovoltaico(form.quantidadeInversores);

  if (!form.marcaModulo || !modulo) {
    return { ok: false, erro: "Selecione o modelo da placa no cadastro de Módulos fotovoltaicos." };
  }
  if (qtdMod == null) {
    return { ok: false, erro: "Informe a quantidade de placas (mínimo 1)." };
  }
  if (!form.marcaInversor || !inversor) {
    return { ok: false, erro: "Selecione o modelo do inversor." };
  }
  if (qtdInv == null) {
    return { ok: false, erro: "Informe a quantidade de inversores (mínimo 1)." };
  }

  const payload: KitFotovoltaicoPdf = {
    modulo_id: modulo.id,
    modulo_marca: modulo.marca,
    modulo_modelo: modulo.modelo,
    modulo_potencia_w: modulo.potenciaW,
    quantidade_modulos: qtdMod,
    inversor_id: inversor.id,
    inversor_marca: inversor.marca,
    inversor_modelo: inversor.modelo,
    inversor_potencia_w: inversor.potenciaW,
    quantidade_inversores: qtdInv,
  };

  return {
    ok: true,
    payload,
    resumo: textoComposicaoKitFotovoltaico({
      quantidadeModulos: qtdMod,
      modulo,
      quantidadeInversores: qtdInv,
      inversor,
    }),
  };
}

/** Produto: Reformas e adequações FV. */
export const ATIVIDADES_PROPOSTA_FV = [
  {
    id: ID_ATIVIDADE_KIT_FOTOVOLTAICO,
    label: "Kit Fotovoltaico",
    hint: "Inclusos módulos, inversor(es) e cabos CC. Informe modelo e quantidade de placas e de inversores.",
  },
  { id: "fv_troca_modulos", label: "Troca de módulos" },
  { id: "fv_fornecimento_modulos", label: "Fornecimento de módulos fotovoltaicos" },
  { id: "fv_remocao_placas", label: "Remoção de placas solares" },
  { id: "fv_remocao_estrutura", label: "Remoção de estrutura de fixação" },
  { id: "fv_remocao_infra_cc", label: "Remoção de infra-estrutura elétrica CC" },
  { id: "fv_remocao_infra_ca", label: "Remoção de infra-estrutura elétrica CA" },
  { id: "fv_remocao_inversor", label: "Remoção de inversor" },
  { id: "fv_substituicao_inversor", label: "Substituição de inversor" },
  { id: "fv_instalacao_inversor", label: "Instalação de inversor" },
  { id: "fv_fornecimento_infra_cc", label: "Fornecimento de materiais de infra-estrutura CC" },
  { id: "fv_fornecimento_infra_ca", label: "Fornecimento de materiais de infra-estrutura CA" },
  { id: "fv_exec_infra_cc", label: "Execução de infra-estrutura elétrica CC" },
  { id: "fv_exec_infra_ca", label: "Execução de infra-estrutura elétrica CA" },
  { id: "fv_fornecimento_estrutura", label: "Fornecimento de Estrutura" },
  { id: "fv_exec_estrutura", label: "Execução de estrutura de fixação" },
  { id: "fv_colocacao_placas", label: "Instalação de placas solares" },
  { id: "fv_adequacao_quadro_ca", label: "Adequação de quadro CA" },
  { id: "fv_exec_quadro_ca", label: "Execução de quadro CA" },
  { id: "fv_testes_comissionamento", label: "Testes e Comissionamento" },
  { id: "fv_mao_obra", label: "Mão de obra" },
] as const satisfies readonly AtividadePropostaAdequacoesDef[];

/** Produto: Sistema de Alarme de Incêndio. */
export const ATIVIDADES_PROPOSTA_INCENDIO_ALARME = [
  { id: "inc_projeto_aprovacao", label: "Projeto e Aprovação" },
  {
    id: "inc_sistema_alarme",
    label: "Sistema de Alarme",
    hint: "Item geral do sistema completo conforme PPCI, sem detalhar valores de cada dispositivo.",
  },
  { id: "inc_central_alarme", label: "Central de alarme" },
  { id: "inc_acionadores", label: "Acionadores manuais" },
  { id: "inc_sirenes", label: "Sirenes" },
  { id: "inc_avisadores", label: "Avisadores visuais" },
  { id: "inc_infra", label: "Infraestrutura (eletrodutos e cabeamento)" },
  { id: "inc_instalacao", label: "Instalação e comissionamento" },
  { id: "inc_art", label: "Emissão de ART" },
  { id: "inc_mao_obra", label: "Mão de obra" },
  { id: "inc_desconto", label: "Desconto", isDesconto: true },
] as const satisfies readonly AtividadePropostaAdequacoesDef[];

/** Produto: Sistema de Detecção Automática de Incêndio (SDAI). */
export const ATIVIDADES_PROPOSTA_INCENDIO_SDAI = [
  { id: "inc_projeto_aprovacao", label: "Projeto e Aprovação" },
  { id: "inc_central_alarme", label: "Central de alarme" },
  { id: "inc_detectores", label: "Detectores de fumaça" },
  { id: "inc_acionadores", label: "Acionadores manuais" },
  { id: "inc_sirenes", label: "Sirenes" },
  { id: "inc_avisadores", label: "Avisadores visuais" },
  { id: "inc_infra", label: "Infraestrutura (eletrodutos e cabeamento)" },
  { id: "inc_instalacao", label: "Instalação e comissionamento" },
  { id: "inc_art", label: "Emissão de ART" },
  { id: "inc_mao_obra", label: "Mão de obra" },
  { id: "inc_desconto", label: "Desconto", isDesconto: true },
] as const satisfies readonly AtividadePropostaAdequacoesDef[];

export const IDS_EQUIPAMENTO_INCENDIO = [
  "inc_central_alarme",
  "inc_acionadores",
  "inc_sirenes",
  "inc_avisadores",
  "inc_detectores",
] as const;

export function isIdEquipamentoIncendio(id: string): boolean {
  return (IDS_EQUIPAMENTO_INCENDIO as readonly string[]).includes(id);
}

/** Margem padrão em % sobre o custo ao ativar uma linha: preço = custo × (1 + margem/100). */
export const MARGEM_PADRAO_ATIVIDADE_ADEQUACOES = 70;

export type EstadoLinhaAtividadeProposta = {
  ativo: boolean;
  /** Custo unitário estimado (R$). */
  custo: number;
  /** Margem em % sobre o custo (texto livre no input, como quantidade). */
  margem: string;
  quantidade: string;
  /** Apenas linha de desconto: valor positivo a abater da receita (R$ unitário). */
  valorAbatimento: number;
};

/** Preço unitário de venda a partir do custo e da margem em % sobre o custo. */
export function precoVendaUnitarioAdequacoes(custo: number, margemPercent: number): number {
  const c = Number.isFinite(custo) ? custo : 0;
  const mp = Number.isFinite(margemPercent) && margemPercent >= 0 ? margemPercent : 0;
  return c * (1 + mp / 100);
}

/** Interpreta o texto do campo margem (%); vazio = ainda digitando. */
export function parseMargemPercentString(s: string): number | null {
  const t = String(s ?? "").trim();
  if (t === "") return null;
  const n = parseFloat(t.replace(",", "."));
  if (!Number.isFinite(n)) return null;
  return n;
}

/** Lista de serviços em ordem alfabética (pt-BR); linhas de desconto ficam no final. */
export function atividadesPropostaOrdenadasPorLabel(
  defs: readonly AtividadePropostaAdequacoesDef[]
): AtividadePropostaAdequacoesDef[] {
  const porLabel = (a: AtividadePropostaAdequacoesDef, b: AtividadePropostaAdequacoesDef) =>
    a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" });
  const servicos = defs.filter((d) => !d.isDesconto).slice().sort(porLabel);
  const descontos = defs.filter((d) => d.isDesconto).slice().sort(porLabel);
  return [...servicos, ...descontos];
}

export function estadoInicialAtividades(
  defs: readonly AtividadePropostaAdequacoesDef[]
): Record<string, EstadoLinhaAtividadeProposta> {
  const acc: Record<string, EstadoLinhaAtividadeProposta> = {};
  for (const d of defs) {
    acc[d.id] = {
      ativo: false,
      custo: 0,
      margem: d.isDesconto ? "0" : String(MARGEM_PADRAO_ATIVIDADE_ADEQUACOES),
      quantidade: "1",
      valorAbatimento: 0,
    };
  }
  return acc;
}
